import { Box, Tooltip, Typography, TypographyProps } from '@mui/joy';
import { SxProps } from '@mui/joy/styles/types';
import parse from 'html-react-parser';
import { isString } from 'lodash';
import {
  cloneElement,
  CSSProperties,
  isValidElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { containsHtmlTags, parseString } from 'core/utils';
import { Level, TooltipPosition } from 'theme/enums';

interface IProps extends Omit<TypographyProps, 'lineHeight'> {
  content?: string | JSX.Element;
  children?: ReactNode;
  maxRows?: number;
  followCursor?: boolean;
  setTooltip?: (value: string, shouldDisplayTooltip?: () => boolean) => void;
  lineHeight?: number;
  showMoreLabel?: boolean;
  showMoreSxProps?: CSSProperties;
  defaultPlaceholder?: string;
}

const LINE_HEIGHT = 21;

const TextEllipsis = ({
  content: contentProp,
  children,
  maxRows = 1,
  followCursor = true,
  setTooltip,
  lineHeight = LINE_HEIGHT,
  showMoreLabel = false,
  showMoreSxProps = {},
  defaultPlaceholder = DEFAULT_PLACEHOLDER,
  sx,
  ...rest
}: IProps): JSX.Element => {
  const [displayTooltip, setDisplayTooltip] = useState(false);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const { t } = useTranslation();

  const content = contentProp || children?.[0] || defaultPlaceholder || '';

  const wrapper = useRef<HTMLDivElement>(null);

  const style: SxProps = {
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textWrap: 'balance',
    overflowWrap: 'break-word',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: maxRows,
    lineHeight: `${lineHeight}px`,
    // +2px: with Chrome's standardized line-clamp, a max-height exactly equal to the clamped
    // height culls even the first line at fractional browser zoom (text renders invisible)
    maxHeight: `calc(${maxRows} * ${lineHeight}px + 2px)`,

    ...sx,
  };

  const isContentOverflowing = useCallback(() => {
    const element = wrapper.current;
    if (!element || !content || DEFAULT_PLACEHOLDER === content) {
      return false;
    }

    const { clientHeight, scrollHeight, clientWidth, scrollWidth } = element;

    return scrollHeight > clientHeight || scrollWidth > clientWidth;
  }, [content]);

  const updateTooltipVisibility = useCallback(() => {
    const shouldDisplayTooltip = isContentOverflowing();
    if (setTooltip) {
      const tooltipContent = isString(content) ? parseString(content as string) : content;
      setTooltip(tooltipContent as string, () => shouldDisplayTooltip);

      return;
    }
    setDisplayTooltip(shouldDisplayTooltip);
  }, [content, isContentOverflowing, setTooltip]);

  useEffect(() => {
    updateTooltipVisibility();
  }, [content, setTooltip, updateTooltipVisibility]);

  useEffect(() => {
    const element = wrapper.current;
    if (!element) {
      return;
    }

    const debouncedUpdate = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        updateTooltipVisibility();
      }, 100);
    };

    const resizeObserver = new ResizeObserver(debouncedUpdate);

    resizeObserver.observe(element);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeObserver.unobserve(element);
    };
  }, [updateTooltipVisibility]);

  if (isString(content)) {
    const tooltipContent = displayTooltip ? parseString(content) : '';

    let contentToWrap = (
      <Typography ref={wrapper} level={Level.BODY_SM} sx={style} {...rest}>
        {parseString(content as string)}
      </Typography>
    );

    if (containsHtmlTags(content as string)) {
      contentToWrap = (
        <Box ref={wrapper} sx={style} {...rest}>
          {parse(content as string)}
        </Box>
      );
    }

    return (
      <Tooltip
        title={tooltipContent}
        placement={followCursor ? TooltipPosition.BOTTOM_START : TooltipPosition.BOTTOM}
        followCursor={followCursor}
      >
        {contentToWrap}
      </Tooltip>
    );
  }

  if (isValidElement(content)) {
    const tooltipContent = displayTooltip ? content : '';

    return (
      <Tooltip
        title={tooltipContent}
        placement={followCursor ? TooltipPosition.BOTTOM_START : TooltipPosition.BOTTOM}
        followCursor={followCursor}
      >
        <Box
          sx={
            showMoreLabel
              ? {
                  width: 'fit-content',
                  marginBottom: displayTooltip ? '24px' : 0,

                  '&::after': displayTooltip
                    ? {
                        content: `"${t('labelMore')}"`,
                        display: 'inline-block',
                        position: 'absolute',
                        color: 'primary.plainColor',
                        bottom: '8px',
                        left: '16px',
                        fontWeight: 600,
                        ...showMoreSxProps,
                      }
                    : {},
                }
              : {}
          }
        >
          <Box ref={wrapper} sx={style} {...rest}>
            {cloneElement(content, {
              ref: wrapper,
              ...rest,
            })}
          </Box>
        </Box>
      </Tooltip>
    );
  }

  return content as JSX.Element;
};

export default TextEllipsis;
